import type { ComponentType, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { reloadOnChunkError } from '@/platform/chunk-reload';
import { useAuth } from '@/contexts/auth-context';
import { canViewPath } from '@/lib/permissions';
import { ViteAuthPage } from '@/vite/AuthPage';
import NotFoundPage from '@/routes/not-found';

type PageModule = { default: ComponentType };
type RouteItem = {
  path: string;
  element: ReactElement;
};

const pageModules = import.meta.glob<PageModule>('./pages/**/page.tsx');

const specialRoutePaths = new Set(['/', '/xac-thuc']);

const toRoutePath = (filePath: string) => {
  const routePath = filePath.replace('./pages', '').replace(/\/page\.tsx$/, '');
  return routePath || '/';
};

const normalizeRoutePath = (pathname: string) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
};

function AccessDeniedPage() {
  return (
    <div className='flex h-screen w-screen flex-col items-center justify-center bg-slate-50 p-4'>
      <div className='w-full max-w-md rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-xl'>
        <div className='mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            className='h-10 w-10'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z'
            />
          </svg>
        </div>
        <h2 className='mb-2 text-xl font-semibold text-slate-800'>Bạn không có quyền truy cập</h2>
        <p className='mb-8 text-sm text-slate-600'>
          Bạn không có quyền xem trang này. Vui lòng quay lại hoặc liên hệ quản trị viên để biết thêm chi tiết.
        </p>
        <button
          onClick={() => window.history.back()}
          className='w-full rounded-xl bg-main py-3 font-medium text-white shadow-lg shadow-main/20 transition-all hover:bg-main/90 hover:shadow-main/30 focus:outline-none focus:ring-2 focus:ring-main focus:ring-offset-2'
        >
          Quay lại
        </button>
      </div>
    </div>
  );
}

function ViteHomePage() {
  return <Navigate to='/trang-chu' replace />;
}

function PermissionRoute({ path, children }: { path: string; children: ReactElement }) {
  const { isAdmin, permissions, authInitialized } = useAuth();

  if (!authInitialized) return null;
  if (!canViewPath(path, permissions, isAdmin)) return <AccessDeniedPage />;

  return children;
}

const createRouteElement = (path: string, loadPageModule: () => Promise<PageModule>) => {
  const Page = lazy(() =>
    loadPageModule().catch(error => {
      reloadOnChunkError(error);
      throw error;
    })
  );

  return (
    <PermissionRoute path={path}>
      <Suspense fallback={null}>
        <Page />
      </Suspense>
    </PermissionRoute>
  );
};

const generatedPageRoutes: RouteItem[] = Object.entries(pageModules)
  .map(([filePath, loadPageModule]) => ({
    path: toRoutePath(filePath),
    element: createRouteElement(toRoutePath(filePath), loadPageModule)
  }))
  .filter(route => !specialRoutePaths.has(route.path))
  .sort((left, right) => left.path.localeCompare(right.path));

const knownRoutePaths = new Set([...specialRoutePaths, ...generatedPageRoutes.map(route => route.path)]);

export const isKnownViteRoutePath = (pathname: string) => knownRoutePaths.has(normalizeRoutePath(pathname));

export const routeMap: RouteItem[] = [
  {
    path: '/',
    element: <ViteHomePage />
  },
  {
    path: '/xac-thuc',
    element: <ViteAuthPage />
  },
  ...generatedPageRoutes,
  {
    path: '*',
    element: <NotFoundPage />
  }
];
